import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock, MapPin, Video } from "lucide-react";
import { Modal } from "../../../../shared/ui/modal";
import { useUpcomingSessions } from "../../application/useScheduler";
import { subjectOf } from "../../domain/subject";
import { formatBookingTime } from "../../domain/formatting";
import type {
    ScheduledSession,
    SubjectKind,
    UpcomingSubjectFilter,
} from "../../domain/types";
import PatientFinder, { type PatientOption } from "../../../../shared/ui/patient/PatientFinder";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    timezone: string | null;
    /**
     * Fixed when opened from a patient or couple page. Given one, no filter is
     * offered: the subject is not in question.
     */
    subject?: UpcomingSubjectFilter | null;
    subjectLabel?: string | null;
    /** Filter options, used only when no subject is fixed. */
    options?: PatientOption[];
    optionsFailed?: boolean;
    /** Resolves a subject id to a display name for the list rows. */
    names?: Map<string, string>;
    onSchedule?: () => void;
}

/**
 * Read-only. Rescheduling and cancelling stay on the Scheduler calendar page,
 * where the confirmation flow already lives — reproducing a destructive action
 * here would mean two implementations of it.
 */
export default function UpcomingSessionsModal({
    isOpen,
    onClose,
    timezone,
    subject = null,
    subjectLabel = null,
    options = [],
    optionsFailed = false,
    names,
    onSchedule,
}: Props) {
    const { t } = useTranslation();
    const [filterId, setFilterId] = useState("");

    // A fixed subject wins; otherwise the filter decides, and clearing it
    // restores the unscoped list.
    const chosen = options.find((option) => option.id === filterId) ?? null;
    const activeSubject: UpcomingSubjectFilter | null =
        subject ??
        (chosen
            ? { kind: (chosen.kind ?? "patient") as SubjectKind, id: chosen.id }
            : null);

    // Re-queried, never trimmed client-side: the server bounds the page, so a
    // local filter would silently cap results at whatever the first page held.
    const query = useUpcomingSessions(activeSubject, isOpen);

    const sessions = useMemo(() => query.data ?? [], [query.data]);

    const nameFor = (session: ScheduledSession): string | undefined => {
        // The server resolves the name; the map is only a fallback for a
        // response that predates that.
        if (session.subject_label) return session.subject_label;
        const own = subjectOf(session);
        if (!own) return undefined;
        return names?.get(own.id) ?? subjectLabel ?? undefined;
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={false}
            className="mx-4 max-w-lg rounded-3xl"
        >
            <div className="flex max-h-[80vh] flex-col gap-4 p-5">
                <div>
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                        {t("scheduler.upcoming.title")}
                    </h2>
                    <p className="text-sm text-content-tertiary">
                        {subjectLabel ?? t("scheduler.upcoming.subtitle")}
                    </p>
                </div>

                {!subject &&
                    (optionsFailed ? (
                        <p role="alert" className="text-sm text-red-600">
                            {t("scheduler.upcoming.filterError")}
                        </p>
                    ) : (
                        // The same searchable picker the booking modal uses: a
                        // caseload is too long for a dropdown, and couples need
                        // to be distinguishable from individuals here too.
                        <PatientFinder
                            patients={options}
                            selectedId={filterId || null}
                            onSelect={(id) => setFilterId(id ?? "")}
                            label={t("scheduler.upcoming.filterLabel")}
                        />
                    ))}

                {query.isLoading ? (
                    <p className="py-6 text-center text-sm text-content-tertiary">
                        {t("scheduler.upcoming.loading")}
                    </p>
                ) : query.isError ? (
                    <div className="flex flex-col items-center gap-2 py-6">
                        <p role="alert" className="text-sm text-red-600">
                            {t("scheduler.upcoming.error")}
                        </p>
                        <button
                            type="button"
                            onClick={() => query.refetch()}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm dark:border-gray-700"
                        >
                            {t("scheduler.upcoming.retry")}
                        </button>
                    </div>
                ) : sessions.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 py-6">
                        <CalendarClock className="h-6 w-6 text-gray-300" />
                        <p className="text-sm text-content-tertiary">
                            {t("scheduler.upcoming.empty")}
                        </p>
                        {onSchedule && (
                            <button
                                type="button"
                                onClick={onSchedule}
                                className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white"
                            >
                                {t("scheduler.upcoming.scheduleOne")}
                            </button>
                        )}
                    </div>
                ) : (
                    <ul className="flex flex-col gap-2 overflow-y-auto">
                        {sessions.map((session) => (
                            <li
                                key={session.id}
                                className="flex items-start justify-between gap-3 rounded-2xl border border-gray-200 p-3 dark:border-gray-700"
                            >
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold">
                                        {nameFor(session) ??
                                            t("scheduler.upcoming.unknownSubject")}
                                    </p>
                                    <p className="text-sm text-content-tertiary">
                                        {`${session.date.slice(0, 10)} · ${formatBookingTime(session.date.slice(0, 10), session.hour, timezone)} · ${session.duration} min`}
                                    </p>
                                    {!session.meeting_link && (
                                        <p className="mt-1 flex items-center gap-1.5 text-xs text-content-tertiary">
                                            <MapPin className="h-3.5 w-3.5" />
                                            {t("scheduler.upcoming.inPerson")}
                                        </p>
                                    )}
                                </div>
                                {/* The meeting link as an icon, as on the
                                    week grid and the session cards */}
                                {session.meeting_link && (
                                    <a
                                        href={session.meeting_link}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={t("scheduler.upcoming.joinLink")}
                                        title={t("scheduler.upcoming.joinLink")}
                                        className="shrink-0 rounded-lg p-1.5 text-brand-600 transition-colors hover:bg-brand-50 dark:hover:bg-brand-600/10"
                                    >
                                        <Video className="h-3.5 w-3.5" aria-hidden="true" />
                                    </a>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                <button
                    type="button"
                    onClick={onClose}
                    className="w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 dark:border-gray-700 dark:text-gray-400"
                >
                    {t("common.close")}
                </button>
            </div>
        </Modal>
    );
}
